'use client';
import React, { useState } from 'react';
import { X, ArrowRight, ArrowLeft, CheckCircle2, Mic, BookOpen, FileText, Shield, Link2, Layers, Feather, Star,  } from 'lucide-react';

const WALKTHROUGH_KEY = 'griott_contributor_walkthrough_seen';

// ── Step data ────────────────────────────────────────────────────────────────

const WALL_TOPICS = [
  { label: 'Great Zimbabwe', pct: 87 },
  { label: 'Chimurenga Wars', pct: 72 },
  { label: 'Rozvi Empire', pct: 18 },
  { label: 'Colonial Land Acts', pct: 65 },
  { label: 'Matabeleland', pct: 24 },
  { label: 'Mutapa Kingdom', pct: 58 },
  { label: 'Liberation Struggle', pct: 91 },
  { label: 'Early Iron Age', pct: 11 },
  { label: 'Oral Law', pct: 29 },
  { label: 'Independence 1980', pct: 83 },
];

const CERTAINTY_ITEMS = [
  {
    icon: Mic,
    label: 'Oral Tradition',
    color: '#7B6FA3',
    bg: '#7B6FA318',
    border: '#7B6FA333',
    desc: 'Knowledge passed down through generations by word of mouth. No written source is required — the tradition itself is the record.',
    note: 'No citation needed',
  },
  {
    icon: BookOpen,
    label: 'Sourced Claim',
    color: '#008080',
    bg: '#00808018',
    border: '#00808033',
    desc: 'Based on a written source, archive document, or published record.',
    note: 'At least 1 citation required',
  },
  {
    icon: FileText,
    label: 'Firsthand Account',
    color: '#2C5E3B',
    bg: '#2C5E3B18',
    border: '#2C5E3B33',
    desc: 'A direct personal experience or testimony from someone who witnessed the event.',
    note: 'You or your source must have been present',
  },
];

const CITATION_FIELDS = [
  { label: 'Title', hint: 'Full title of the source' },
  { label: 'Author', hint: 'Author name(s) or institution' },
  { label: 'Publication date', hint: 'Year or full date' },
  { label: 'URL / Archive ref', hint: 'Link or archive call number' },
  { label: 'Document type', hint: 'Book, journal, newspaper, etc.' },
];

const SUBMISSION_STEPS = [
  {
    icon: Feather,
    label: 'Write your contribution',
    desc: 'Compose your story in the rich-text editor. Minimum 50 words. You can save a draft at any time.',
    color: '#C17A3E',
  },
  {
    icon: Layers,
    label: 'Tag era, topic & region',
    desc: 'Choose the historical era, topic, and Zimbabwean region your contribution belongs to.',
    color: '#7B6FA3',
  },
  {
    icon: Star,
    label: 'Pick a certainty label',
    desc: 'Select Oral Tradition, Sourced Claim, or Firsthand Account — this is shown publicly.',
    color: '#008080',
  },
  {
    icon: Link2,
    label: 'Add citations (if required)',
    desc: 'Sourced Claims require at least one citation. Oral Tradition and Firsthand Account do not.',
    color: '#2C5E3B',
  },
  {
    icon: Shield,
    label: 'Submit for curator review',
    desc: 'A human curator reads your submission. Decisions take 3–7 days. You\'ll be notified.',
    color: '#CC0000',
  },
];

// ── Sub-components ────────────────────────────────────────────────────────────

function StepDots({ total, current }: { total: number; current: number }) {
  return (
    <div className="flex gap-1.5">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className="rounded-full transition-all duration-300"
          style={{
            width: i === current ? 20 : 6,
            height: 6,
            backgroundColor: i <= current ? 'var(--primary)' : 'var(--muted)',
          }}
        />
      ))}
    </div>
  );
}

// ── Step 1: The Wall Metaphor ─────────────────────────────────────────────────

function StepWall() {
  return (
    <div>
      <p className="text-sm leading-relaxed mb-5" style={{ color: 'var(--muted-foreground)' }}>
        Griott visualises African history as a <strong style={{ color: 'var(--foreground)' }}>Wall of Knowledge</strong> — a mosaic where each stone represents a documented topic. Filled stones are well-covered; dashed outlines are gaps waiting to be filled.
      </p>

      {/* Mini wall grid */}
      <div className="grid grid-cols-5 gap-1.5 mb-5">
        {WALL_TOPICS.map((t) => {
          const filled = t.pct >= 50;
          return (
            <div
              key={t.label}
              className="rounded-lg p-2 flex flex-col items-center justify-center text-center"
              style={{
                border: filled ? '1px solid rgba(193,122,62,0.35)' : '1.5px dashed rgba(193,122,62,0.3)',
                backgroundColor: filled ? `rgba(193,122,62,${(t.pct / 100) * 0.18 + 0.04})` : 'transparent',
                minHeight: 52,
              }}
            >
              <span className="text-[9px] leading-tight font-medium" style={{ color: filled ? 'var(--foreground)' : 'var(--muted-foreground)' }}>
                {t.label}
              </span>
              <span
                className="text-[10px] font-bold mt-0.5"
                style={{ color: filled ? 'var(--primary)' : 'rgba(193,122,62,0.45)' }}
              >
                {t.pct}%
              </span>
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-4 text-xs" style={{ color: 'var(--muted-foreground)' }}>
        <span className="flex items-center gap-1.5">
          <span
            className="inline-block w-3 h-3 rounded-sm"
            style={{ backgroundColor: 'rgba(193,122,62,0.22)', border: '1px solid rgba(193,122,62,0.35)' }}
          />
          Filled stone — well documented
        </span>
        <span className="flex items-center gap-1.5">
          <span
            className="inline-block w-3 h-3 rounded-sm"
            style={{ border: '1.5px dashed rgba(193,122,62,0.3)' }}
          />
          Dashed gap — needs contributions
        </span>
      </div>
    </div>
  );
}

// ── Step 2: Submission Process ────────────────────────────────────────────────

function StepProcess() {
  return (
    <div>
      <p className="text-sm leading-relaxed mb-5" style={{ color: 'var(--muted-foreground)' }}>
        Every contribution follows the same five-step path from your keyboard to the public archive.
      </p>
      <div className="space-y-2.5">
        {SUBMISSION_STEPS.map((s, i) => (
          <div
            key={s.label}
            className="flex items-start gap-3 p-3.5 rounded-xl border"
            style={{ backgroundColor: `${s.color}0a`, borderColor: `${s.color}28` }}
          >
            <div
              className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
              style={{ backgroundColor: `${s.color}1a`, color: s.color }}
            >
              <s.icon size={14} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <span
                  className="text-[10px] font-bold font-mono"
                  style={{ color: s.color, opacity: 0.7 }}
                >
                  {String(i + 1).padStart(2, '0')}
                </span>
                <p className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>{s.label}</p>
              </div>
              <p className="text-xs leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>{s.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Step 3: Citation Requirements ────────────────────────────────────────────

function StepCitations() {
  return (
    <div>
      <p className="text-sm leading-relaxed mb-4" style={{ color: 'var(--muted-foreground)' }}>
        Citations let curators verify your sources and give readers a path to the original record. They are <strong style={{ color: 'var(--foreground)' }}>required for Sourced Claims</strong> and optional for other labels.
      </p>

      {/* Fields preview */}
      <div className="rounded-xl border overflow-hidden mb-4" style={{ borderColor: 'var(--border)' }}>
        <div
          className="px-4 py-2.5 border-b flex items-center gap-2"
          style={{ backgroundColor: 'var(--muted)', borderColor: 'var(--border)' }}
        >
          <Link2 size={13} style={{ color: 'var(--primary)' }} />
          <span className="text-xs font-semibold" style={{ color: 'var(--foreground)' }}>Citation fields</span>
        </div>
        <div className="divide-y" style={{ divideColor: 'var(--border)' }}>
          {CITATION_FIELDS.map((f) => (
            <div key={f.label} className="flex items-center justify-between px-4 py-2.5">
              <span className="text-xs font-medium" style={{ color: 'var(--foreground)' }}>{f.label}</span>
              <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{f.hint}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Tips */}
      <div className="space-y-2">
        <div
          className="flex items-start gap-2.5 p-3 rounded-lg border text-xs"
          style={{ backgroundColor: '#C17A3E0d', borderColor: '#C17A3E2a' }}
        >
          <CheckCircle2 size={13} className="shrink-0 mt-0.5" style={{ color: '#C17A3E' }} />
          <span style={{ color: 'var(--muted-foreground)' }}>
            <strong style={{ color: 'var(--foreground)' }}>AI citation helper</strong> — paste a URL or DOI and Griott will auto-fill the fields for you.
          </span>
        </div>
        <div
          className="flex items-start gap-2.5 p-3 rounded-lg border text-xs"
          style={{ backgroundColor: '#2C5E3B0d', borderColor: '#2C5E3B2a' }}
        >
          <CheckCircle2 size={13} className="shrink-0 mt-0.5" style={{ color: '#2C5E3B' }} />
          <span style={{ color: 'var(--muted-foreground)' }}>
            <strong style={{ color: 'var(--foreground)' }}>Broken links</strong> — if a URL is unreachable, curators will flag it. Use archive references where possible.
          </span>
        </div>
      </div>
    </div>
  );
}

// ── Step 4: Certainty Levels ──────────────────────────────────────────────────

function StepCertainty() {
  return (
    <div>
      <p className="text-sm leading-relaxed mb-4" style={{ color: 'var(--muted-foreground)' }}>
        Every contribution carries one of three certainty labels — shown publicly so readers know how to weigh each record. Choose the label that <strong style={{ color: 'var(--foreground)' }}>honestly describes your source</strong>.
      </p>
      <div className="space-y-2.5">
        {CERTAINTY_ITEMS.map((item) => (
          <div
            key={item.label}
            className="flex items-start gap-3 p-4 rounded-xl border"
            style={{ backgroundColor: item.bg, borderColor: item.border }}
          >
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
              style={{ backgroundColor: `${item.color}22`, color: item.color }}
            >
              <item.icon size={15} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2 mb-0.5 flex-wrap">
                <p className="text-sm font-semibold" style={{ color: item.color }}>{item.label}</p>
                <span
                  className="text-[10px] font-medium px-2 py-0.5 rounded-full"
                  style={{ backgroundColor: `${item.color}18`, color: item.color }}
                >
                  {item.note}
                </span>
              </div>
              <p className="text-xs leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>{item.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Step 5: Ready ─────────────────────────────────────────────────────────────

function StepReady() {
  return (
    <div className="text-center py-2">
      {/* Emblem */}
      <div
        className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5"
        style={{ backgroundColor: 'rgba(193,122,62,0.12)', border: '1px solid rgba(193,122,62,0.25)' }}
      >
        <Feather size={28} style={{ color: 'var(--primary)' }} />
      </div>

      <h3 className="text-lg font-bold mb-2" style={{ color: 'var(--foreground)', fontFamily: 'var(--font-serif, serif)' }}>
        You're ready to contribute
      </h3>
      <p className="text-sm leading-relaxed mb-6 max-w-sm mx-auto" style={{ color: 'var(--muted-foreground)' }}>
        Every stone you add strengthens the Wall. Your knowledge — oral, sourced, or firsthand — belongs in the archive.
      </p>

      {/* Quick-reference chips */}
      <div className="grid grid-cols-2 gap-2 text-left">
        {[
          { icon: Layers, label: 'Tag era & topic', color: '#7B6FA3' },
          { icon: Star, label: 'Pick certainty label', color: '#008080' },
          { icon: Link2, label: 'Cite sources if needed', color: '#C17A3E' },
          { icon: Shield, label: 'Curator reviews in 3–7 days', color: '#2C5E3B' },
        ].map((chip) => (
          <div
            key={chip.label}
            className="flex items-center gap-2 p-2.5 rounded-lg border"
            style={{ backgroundColor: `${chip.color}0d`, borderColor: `${chip.color}28` }}
          >
            <chip.icon size={13} style={{ color: chip.color, flexShrink: 0 }} />
            <span className="text-xs font-medium" style={{ color: 'var(--foreground)' }}>{chip.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

const STEPS = [
  { title: 'The Wall metaphor', subtitle: 'How Griott maps history', component: StepWall },
  { title: 'Submission process', subtitle: 'Five steps from draft to archive', component: StepProcess },
  { title: 'Citation requirements', subtitle: 'When and how to cite sources', component: StepCitations },
  { title: 'Certainty levels', subtitle: 'Label your source honestly', component: StepCertainty },
  { title: 'You\'re all set', subtitle: 'Quick reference before you start', component: StepReady },
];

interface ContributorWalkthroughProps {
  onClose: () => void;
}

export default function ContributorWalkthrough({ onClose }: ContributorWalkthroughProps) {
  const [step, setStep] = useState(0);

  const dismiss = () => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(WALKTHROUGH_KEY, 'seen');
    }
    onClose();
  };

  const isLast = step === STEPS.length - 1;
  const StepContent = STEPS[step].component;

  return (
    <div
      className="fixed inset-0 z-[95] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(2px)' }}
    >
      <div
        className="w-full max-w-lg rounded-2xl border shadow-2xl overflow-hidden flex flex-col"
        style={{
          backgroundColor: 'var(--card)',
          borderColor: 'var(--border)',
          maxHeight: 'calc(100vh - 2rem)',
        }}
      >
        {/* ── Header ── */}
        <div
          className="flex items-start justify-between px-6 pt-5 pb-4 border-b shrink-0"
          style={{ borderColor: 'var(--border)' }}
        >
          <div>
            <p
              className="text-[10px] font-mono uppercase tracking-widest mb-1"
              style={{ color: 'var(--primary)', fontFamily: 'var(--font-mono)' }}
            >
              Contributor walkthrough · {step + 1} of {STEPS.length}
            </p>
            <h2 className="text-lg font-bold leading-tight" style={{ color: 'var(--foreground)', fontFamily: 'var(--font-serif, serif)' }}>
              {STEPS[step].title}
            </h2>
            <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
              {STEPS[step].subtitle}
            </p>
          </div>
          <button
            onClick={dismiss}
            className="p-1.5 rounded-lg transition-colors duration-150 shrink-0 ml-4"
            style={{ color: 'var(--muted-foreground)' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--muted)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'; }}
            aria-label="Close walkthrough"
          >
            <X size={17} />
          </button>
        </div>

        {/* ── Progress dots ── */}
        <div className="px-6 pt-3 pb-1 shrink-0">
          <StepDots total={STEPS.length} current={step} />
        </div>

        {/* ── Scrollable content ── */}
        <div className="px-6 py-4 overflow-y-auto flex-1">
          <StepContent />
        </div>

        {/* ── Footer ── */}
        <div
          className="flex items-center justify-between px-6 py-4 border-t shrink-0"
          style={{ borderColor: 'var(--border)', backgroundColor: 'var(--muted)' }}
        >
          <button
            onClick={dismiss}
            className="text-xs"
            style={{ color: 'var(--muted-foreground)' }}
          >
            Skip walkthrough
          </button>

          <div className="flex items-center gap-2">
            {step > 0 && (
              <button
                onClick={() => setStep((s) => s - 1)}
                className="flex items-center gap-1 px-3.5 py-2 rounded-lg text-sm font-medium border transition-all duration-150 active:scale-95"
                style={{ borderColor: 'var(--border)', color: 'var(--foreground)', backgroundColor: 'var(--card)' }}
              >
                <ArrowLeft size={13} /> Back
              </button>
            )}
            {isLast ? (
              <button
                onClick={dismiss}
                className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm font-semibold transition-all duration-150 active:scale-95"
                style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
              >
                <CheckCircle2 size={14} /> Start contributing
              </button>
            ) : (
              <button
                onClick={() => setStep((s) => s + 1)}
                className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm font-semibold transition-all duration-150 active:scale-95"
                style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
              >
                Next <ArrowRight size={14} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export { WALKTHROUGH_KEY };
